import UdeskLocales from 'UdeskLocales';
import React, { FC, memo, useState } from 'react';
import { useRequest } from 'ahooks';
import { Button, Input, message, Modal, Space, Spin } from 'udesk-ui';
import { postAiModelsPromptPolish } from 'src/api/aiModels/prompt/polish';
import { getPromptTasksById } from 'src/api/promptTasks/{id}';
import { isEmpty } from 'src/util/core';
import { PostAiModelsPromptPolishBody } from 'src/api/types';
import { TypeEnum } from 'Component/pages/semantic-intelligence/intelligent-model/const';

interface TemplateProps {
    onApply?: (p?: string) => void;
    getParams?: () => any;
}

const Template: FC<TemplateProps> = memo((props) => {
    const { onApply: _onApply, getParams } = props;

    const [visible, setVisible] = useState(false);

    const onOptPrompt = () => {
        const { modelDesc, typeSelect } = getParams?.() || {};

        const { type, divideRuleList } = typeSelect || {};

        if (
            !modelDesc ||
            (type === TypeEnum.GradedEvaluation &&
                !divideRuleList?.every((e) => e.divide && e.divideDesc))
        ) {
            message.warning(
                /* 请完善配置 */ UdeskLocales['current'].pages.semanticIntelligence.intelligentModel
                    .components.promptArea.index.pleaseImproveTheConfiguration
            );
            return;
        }

        setVisible(true);
    };

    const onCancel = () => {
        setVisible(false);
        cancelGetPrompt();
    };

    const [instruction, setInstruction] = useState('');

    const [prompt, setPrompt] = useState<string>();

    const { run: getPrompt, cancel: cancelGetPrompt } = useRequest(
        (id) => getPromptTasksById({ segments: { id } }),
        {
            manual: true,
            pollingInterval: 2000,
            onSuccess: (res) => {
                if (res.data?.status === 5) {
                    cancelGetPrompt();
                    setPrompt(res.data.result);
                    setIsLoading(false);
                } else if (res.data?.status === 4) {
                    cancelGetPrompt();
                    message.error(
                        /* 优化提示语失败 */ UdeskLocales['current'].pages.semanticIntelligence
                            .intelligentModel.components.promptArea.components.optimization.index
                            .failedToOptimizePrompt
                    );
                    setIsLoading(false);
                }
            },
            onError: () => {
                cancelGetPrompt();
                setIsLoading(false);
            },
        }
    );

    const { run: onPolish } = useRequest(
        (v: PostAiModelsPromptPolishBody) => postAiModelsPromptPolish(v),
        {
            manual: true,
            onSuccess: (res) => {
                if (res.data) {
                    getPrompt(res.data);
                } else {
                    setIsLoading(false);
                }
            },
            onError: () => {
                setIsLoading(false);
            },
        }
    );

    const [isLoading, setIsLoading] = useState(false);

    const onGen = () => {
        const { modelDesc, name, status, typeSelect } = getParams?.() || {};

        const { type, divideRuleList } = typeSelect || {};

        if (isEmpty(instruction)) {
            message.warning(
                /* 请输入指令 */ UdeskLocales['current'].pages.semanticIntelligence.intelligentModel
                    .components.promptArea.components.optimization.index.pleaseEnterACommand
            );
            return;
        }

        setIsLoading(true);

        onPolish({
            name,
            modelDesc: modelDesc || name,
            status,
            type,
            divideRuleList,
            prompt: instruction,
        });
    };

    const onApply = () => {
        if (!prompt?.trim()) {
            message.warning(
                /* 请先生成提示词 */ UdeskLocales['current'].pages.semanticIntelligence
                    .intelligentModel.components.promptArea.components.optimization.index
                    .pleaseBecomeAPromptWord
            );
            return;
        }
        _onApply?.(prompt);
        onCancel();
    };

    return (
        <>
            <Button type="primary" onClick={onOptPrompt}>
                {/* 优化提示语 */}
                {
                    UdeskLocales['current'].pages.semanticIntelligence.intelligentModel.components
                        .promptArea.components.optimization.index.optimizationPrompt
                }
            </Button>

            <Modal
                title={
                    <div>
                        <Space align="baseline">
                            <div>
                                {/* 优化提示语 */}
                                {
                                    UdeskLocales['current'].pages.semanticIntelligence
                                        .intelligentModel.components.promptArea.components
                                        .optimization.index.optimizationPrompt
                                }
                            </div>
                            <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
                                {/* 若提示词中包含变量，提示词优化后，变量需重新插入 */}
                                {
                                    UdeskLocales['current'].pages.semanticIntelligence
                                        .intelligentModel.components.promptArea.components
                                        .optimization.index
                                        .ifThePromptContainsVariablesTheVariablesNeedToBeReinsertedAfterThePromptIsOptimized
                                }
                            </div>
                        </Space>
                    </div>
                }
                visible={visible}
                width={900}
                footer={
                    <Space>
                        <Button onClick={() => onCancel()}>
                            {/* 取消 */}
                            {
                                UdeskLocales['current'].pages.semanticIntelligence.intelligentModel
                                    .components.promptArea.components.optimization.index.cancel
                            }
                        </Button>
                        <Button type="primary" onClick={() => onApply()} disabled={isLoading}>
                            {/* 应用 */}
                            {
                                UdeskLocales['current'].pages.semanticIntelligence.intelligentModel
                                    .components.promptArea.components.optimization.index.application
                            }
                        </Button>
                    </Space>
                }
                onCancel={() => onCancel()}
            >
                <Spin spinning={isLoading}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                        <div style={{ width: '50%' }}>
                            <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
                                {/* 指令 */}
                                {
                                    UdeskLocales['current'].pages.semanticIntelligence
                                        .intelligentModel.components.promptArea.components
                                        .optimization.index.instruction
                                }
                            </div>
                            <Input.TextArea
                                placeholder={
                                    /* 请输入指令 */ UdeskLocales['current'].pages
                                        .semanticIntelligence.intelligentModel.components.promptArea
                                        .components.optimization.index.pleaseEnterACommand
                                }
                                rows={16}
                                style={{
                                    resize: 'none',
                                    backgroundColor: 'rgba(0,0,0,0.05)',
                                }}
                                value={instruction}
                                onChange={(e) => setInstruction(e.target.value)}
                            />
                        </div>

                        <Button type="primary" onClick={() => onGen()}>
                            {/* 生成 */}
                            {
                                UdeskLocales['current'].pages.semanticIntelligence.intelligentModel
                                    .components.promptArea.components.optimization.index.generate
                            }
                        </Button>

                        <div style={{ width: '50%' }}>
                            <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
                                {/* 生成的提示词 */}
                                {
                                    UdeskLocales['current'].pages.semanticIntelligence
                                        .intelligentModel.components.promptArea.components
                                        .optimization.index.promptWordGenerated
                                }
                            </div>
                            <Input.TextArea
                                autoSize={false}
                                rows={16}
                                style={{ resize: 'none' }}
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                            />
                        </div>
                    </div>
                </Spin>
            </Modal>
        </>
    );
});

export default Template;
